import type {
  IBrandsApiResponse,
  IModelDetailResponse,
  IModelsApiResponse
} from '@/pages/AutoCreditOrder/types';

import { ApiConnector, apiConnector } from './api.connector';

export interface IGetModelsParams {
  dealerGroupId: string;
  leasingCompanyId?: string;
  brandId?: string;
  limit?: number;
  offset?: number;
  modelLimitPerBrand?: number;
}

export interface IGetBrandsParams {
  dealerGroupId: string;
  leasingCompanyId?: string;
}

export interface IGetModelDetailParams {
  dealerGroupId: string;
  leasingCompanyId?: string;
}

export interface ICreateRequestParams {
  personalId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  clientPhone: string;
  dealerGroupId: string;
  dealerId: string;
  equipmentId: string;
  engineTypeId: string;
  repaymentTypeId: string;
  colorId: string;
  carYear: number;
  requestAmount: number;
  loanPeriod: number;
  initFee: number;
  is_leasing?: boolean;
  is_terms_accepted?: boolean;
}

export interface ICreateRequestResponse {
  request_uuid: string;
  otp_expires_at?: string;
  success?: boolean;
  request_id?: string;
  redirect_url?: string;
  status?: string;
}

export interface IVerifyOtpResponse {
  success: boolean;
  request_uuid: string;
  biometric_reference_id: string | null;
  access_token: string;
  refresh_token: string;
}

export interface IClientInfo {
  uuid: string;
  personal_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  phone_number: string;
}

export interface IVerifyClientResponse {
  exists: boolean;
  client: IClientInfo | null;
}

export interface IDealerGroupResponse {
  uuid: string;
  name: string;
  logo_url: string;
}

export interface ILeasingCalculateParams {
  pinfl: string;
  down_payment: number;
  duration: number;
  price: number;
  type: 'sale' | 'leasing';
  dealer_tin?: string;
  fuel_code?: number;
  brand_name?: string;
}

export interface ILeasingCalculateResponse {
  order_price: number;
  total_price: number;
  installment_price: number;
  registration_fee: number;
  markup: number;
  month: number;
  term: 'short' | 'long';
}

export interface ILeasingCalculateEnvelope {
  data: ILeasingCalculateResponse;
}

export type LeasingRequestType = 'idjara' | 'murabaha';

export interface ICreateLeasingParams {
  dealerId: string;
  equipmentId: string;
  colorId: string;
  carYear: number;
  engineTypeId: string;
  loanPeriod: number;
  initFee: number;
  requestAmount?: number;
  leasingCompanyId?: string;
  orderPrice: number;
  approvedAmount: number;
  monthlyPayment: number;
  markup: number;
  term: 'short' | 'long';
  is_terms_accepted: boolean;
  leasingType: LeasingRequestType;
}

export interface ICreateLeasingResponse {
  success?: boolean;
  request_id?: string;
  dealer_group_uuid?: string;
  redirect_url?: string;
  status?: string;
}

class OnlineApi extends ApiConnector {
  async getModels(params: IGetModelsParams): Promise<IModelsApiResponse> {
    return apiConnector.call<IGetModelsParams, IModelsApiResponse>({
      path: 'online-utils/models',
      method: 'get',
      params
    });
  }

  async getBrands(params: IGetBrandsParams): Promise<IBrandsApiResponse> {
    return apiConnector.call<IGetBrandsParams, IBrandsApiResponse>({
      path: 'online-utils/brands',
      method: 'get',
      params
    });
  }

  async getModelDetail(
    modelId: string,
    params: IGetModelDetailParams
  ): Promise<IModelDetailResponse> {
    return apiConnector.call<IGetModelDetailParams, IModelDetailResponse>({
      path: `online-utils/${modelId}`,
      method: 'get',
      params
    });
  }

  async createRequest(
    data: ICreateRequestParams
  ): Promise<ICreateRequestResponse> {
    return apiConnector.call<ICreateRequestParams, ICreateRequestResponse>({
      path: 'online-requests/create-otp',
      method: 'post',
      body: data
    });
  }

  async createLeasingRequest(
    data: ICreateLeasingParams,
    accessToken: string
  ): Promise<ICreateLeasingResponse> {
    return apiConnector.call<ICreateLeasingParams, ICreateLeasingResponse>({
      path: 'online-requests/create-leasing',
      method: 'post',
      body: data,
      tokenOverride: accessToken
    });
  }

  async verifyOtp(
    requestId: string,
    otp: string,
    isLeasing: boolean
  ): Promise<IVerifyOtpResponse> {
    return apiConnector.call<
      { otp: string; is_leasing: boolean },
      IVerifyOtpResponse
    >({
      path: `online-requests/${requestId}/verify-otp`,
      method: 'post',
      body: { otp, is_leasing: isLeasing }
    });
  }

  async resendOtp(requestId: string): Promise<IVerifyOtpResponse> {
    return apiConnector.call<undefined, IVerifyOtpResponse>({
      path: `online-requests/${requestId}/resend-otp`,
      method: 'post'
    });
  }

  async verifyClient(
    pinfl: string,
    dealerGroupId: string
  ): Promise<IVerifyClientResponse> {
    return apiConnector.call<
      { pinfl: string; dealerGroupId: string },
      IVerifyClientResponse
    >({
      path: 'online-utils/verify-client',
      method: 'post',
      body: { pinfl, dealerGroupId }
    });
  }

  async getDealerGroup(dealerGroupId: string): Promise<IDealerGroupResponse> {
    return apiConnector.call<undefined, IDealerGroupResponse>({
      path: `online-clients/dealer-group/${dealerGroupId}`,
      method: 'get'
    });
  }

  async calculateLeasing(
    data: ILeasingCalculateParams
  ): Promise<ILeasingCalculateEnvelope> {
    return apiConnector.call<
      ILeasingCalculateParams,
      ILeasingCalculateEnvelope
    >({
      path: 'online-requests/iman/calculate',
      method: 'post',
      body: data
    });
  }
}

export const onlineApi = new OnlineApi();
